import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useRef } from "react";
import { Link, useLocation } from "react-router";
import { api, type Me } from "./api";
import { useForgeEvent } from "./hooks";
import { modeChip } from "./modeChip";

interface NavItem {
  to: string;
  label: string;
  icon: string;
  admin?: boolean;
}

export const NAV: NavItem[] = [
  { to: "/", label: "Home", icon: "home" },
  { to: "/keys", label: "My keys", icon: "vpn_key" },
  { to: "/setup", label: "Set up a tool", icon: "code" },
  { to: "/models", label: "Models", icon: "psychology" },
  { to: "/logs", label: "Request log", icon: "receipt_long" },
  { to: "/service-keys", label: "Service keys", icon: "badge", admin: true },
  { to: "/admin", label: "Administration", icon: "admin_panel_settings", admin: true },
];

export function Shell({ me, children }: { me: Me; children: ReactNode }) {
  const location = useLocation();
  const qc = useQueryClient();
  const profile = useRef<HTMLElement>(null);
  useForgeEvent(profile, "forge-user-profile-sign-out", async () => {
    const { logoutUrl } = await api.signOut();
    qc.clear();
    if (logoutUrl) window.location.href = logoutUrl; // also ends the Okta session
    else await qc.invalidateQueries({ queryKey: ["me"] });
  });

  const active = (to: string) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));
  const title = [...NAV].reverse().find((n) => active(n.to))?.label ?? "";
  const email = me.user.email ?? me.user.userId;

  const chip = modeChip(me);
  return (
    <forge-app-layout app-title="AI Gateway Access" app-title-href="/">
      <forge-icon slot="app-bar-logo" name="vpn_key" />

      <div slot="app-bar-end" className="row gap-xsmall">
        {chip && <span className={`mode-pill ${chip.snapshot ? "mode-pill--snapshot" : ""}`} title={chip.title}>{chip.label}</span>}
        <forge-user-profile ref={profile} full-name={email.split("@")[0]} email={email} button-label="Open your profile menu" theme-toggle>
          <span slot="sign-out-button-text">Sign out</span>
        </forge-user-profile>
      </div>

      <forge-list slot="navigation" navlist>
        {NAV.filter((n) => !n.admin || me.isAdmin).map((n) => (
          <forge-list-item key={n.to} selected={active(n.to) || undefined}>
            <forge-icon slot="start" name={n.icon} />
            <Link to={n.to}>{n.label}</Link>
          </forge-list-item>
        ))}
      </forge-list>

      <div slot="body-header">
        <forge-toolbar>
          <h1 slot="start" className="forge-typography--heading4">{title}</h1>
        </forge-toolbar>
      </div>

      <main slot="body" className="page">
        {me.readOnly && (
          <forge-inline-message theme="info" className="mb-medium">
            <forge-icon slot="icon" name="info" />
            <div slot="title">You are looking at a snapshot of production</div>
            <span>Nothing here can change the gateway. Creating, rotating and revoking keys is switched off.</span>
          </forge-inline-message>
        )}
        {children}
      </main>

      <footer slot="body-footer">
        <forge-toolbar inverted>
          <div slot="center" className="forge-typography--body1">
            Tyler Technologies AI Gateway · costs are LiteLLM list-price estimates · build {import.meta.env.VITE_BUILD_ID ?? "dev"}
          </div>
        </forge-toolbar>
      </footer>
    </forge-app-layout>
  );
}
